/* Zenfusion Flow — direct-response product page blocks (main-product zf_* blocks + sticky ATC).
   Builds on Dawn's pub/sub (assets/pubsub.js + constants.js) and product-info.js. Vanilla JS, no dependencies. */
(() => {
  if (window.ZFProduct) return;
  const ZF = (window.ZFProduct = {});
  const hasPubSub = typeof subscribe === 'function' && typeof PUB_SUB_EVENTS !== 'undefined';

  /* ---------- Helpers ---------- */
  ZF.formatMoney = (cents, format) => {
    const fmt = format || '${{amount}}';
    const value = Math.round(Number(cents) || 0);
    const split = (n, decimals, thousands, decimal) => {
      const fixed = (n / 100).toFixed(decimals);
      const parts = fixed.split('.');
      const whole = parts[0].replace(/\B(?=(\d{3})+(?!\d))/g, thousands);
      return parts[1] ? whole + decimal + parts[1] : whole;
    };
    return fmt.replace(/\{\{\s*(\w+)\s*\}\}/, (_, key) => {
      switch (key) {
        case 'amount_no_decimals': return split(value, 0, ',', '.');
        case 'amount_with_comma_separator': return split(value, 2, '.', ',');
        case 'amount_no_decimals_with_comma_separator': return split(value, 0, '.', ',');
        case 'amount_with_apostrophe_separator': return split(value, 2, "'", '.');
        case 'amount_with_space_separator': return split(value, 2, ' ', ',');
        case 'amount_no_decimals_with_space_separator': return split(value, 0, ' ', ',');
        default: return split(value, 2, ',', '.');
      }
    });
  };

  ZF.sectionOf = (pi) => (pi ? pi.dataset.section : '');
  ZF.originalSectionOf = (pi) => (pi ? pi.dataset.originalSection || pi.dataset.section : '');
  ZF.currencyRate = () => parseFloat(window.Shopify && Shopify.currency && Shopify.currency.rate) || 1;
  ZF.fetchCart = () =>
    fetch(`${(window.routes && routes.cart_url) || '/cart'}.js`, { headers: { Accept: 'application/json' } }).then((r) => r.json());

  /* Copy every [data-zf-sync] element's markup from freshly rendered section HTML (variant change). */
  ZF.syncFromHtml = (pi, html) => {
    if (!pi || !html) return;
    const current = ZF.sectionOf(pi);
    const original = ZF.originalSectionOf(pi);
    pi.querySelectorAll('[data-zf-sync][id]').forEach((el) => {
      const id = current !== original ? el.id.split(current).join(original) : el.id;
      const source = html.getElementById(id);
      if (source) el.innerHTML = source.innerHTML;
    });
  };

  /* Re-apply the section's ATC label override after Dawn resets the button text. */
  ZF.applyAtcLabel = (pi) => {
    const sync = pi && pi.querySelector('zf-product-sync');
    const label = sync && sync.dataset.atcLabel;
    if (!label) return;
    const btn = pi.querySelector(`#ProductSubmitButton-${ZF.sectionOf(pi)}`);
    if (!btn || btn.hasAttribute('disabled')) return;
    const span = btn.querySelector('span');
    if (span && !span.classList.contains('hidden')) span.textContent = label;
  };

  /* ---------- Central pub/sub bridge ---------- */
  if (hasPubSub) {
    subscribe(PUB_SUB_EVENTS.optionValueSelectionChange, ({ data } = {}) => {
      const target = data && data.event && data.event.target;
      ZF.lastProductInfo = target && target.closest ? target.closest('product-info') : null;
    });

    subscribe(PUB_SUB_EVENTS.variantChange, ({ data } = {}) => {
      if (!data) return;
      let targets = [];
      if (ZF.lastProductInfo && ZF.lastProductInfo.isConnected) targets = [ZF.lastProductInfo];
      else
        targets = Array.from(document.querySelectorAll('product-info')).filter(
          (pi) => ZF.originalSectionOf(pi) === data.sectionId && !pi.closest('quick-add-modal')
        );
      targets.forEach((pi) => {
        ZF.syncFromHtml(pi, data.html);
        ZF.applyAtcLabel(pi);
        pi.dispatchEvent(new CustomEvent('zf:variant-change', { detail: data }));
      });
    });
  }

  /* ---------- <zf-product-sync>: label override on load ---------- */
  if (!customElements.get('zf-product-sync')) {
    customElements.define(
      'zf-product-sync',
      class extends HTMLElement {
        connectedCallback() {
          const pi = this.closest('product-info');
          // product-form.js may run after us; apply on next frame as well.
          ZF.applyAtcLabel(pi);
          requestAnimationFrame(() => ZF.applyAtcLabel(pi));
        }
      }
    );
  }

  /* ---------- <zf-pack-options>: quantity break cards ---------- */
  if (!customElements.get('zf-pack-options')) {
    customElements.define(
      'zf-pack-options',
      class ZfPackOptions extends HTMLElement {
        connectedCallback() {
          this.pi = this.closest('product-info');
          this.price = parseInt(this.dataset.price, 10) || 0;
          this.compare = parseInt(this.dataset.compare, 10) || 0;
          this.onChange = (e) => {
            if (e.target.matches('.zf-ppack__radio')) this.apply();
          };
          this.onVariant = (e) => {
            const v = e.detail && e.detail.variant;
            if (v) {
              this.price = v.price || 0;
              this.compare = v.compare_at_price || 0;
              this.render();
            }
            this.apply();
          };
          this.onQtyInput = (e) => {
            if (e.target === this.qtyInput && !this.applying) this.syncRadios();
          };
          this.addEventListener('change', this.onChange);
          this.pi && this.pi.addEventListener('zf:variant-change', this.onVariant);
          this.pi && this.pi.addEventListener('change', this.onQtyInput);
          if (hasPubSub) this.unsubQty = subscribe(PUB_SUB_EVENTS.quantityUpdate, () => this.apply());
          this.render();
          this.apply();
        }

        disconnectedCallback() {
          this.removeEventListener('change', this.onChange);
          this.pi && this.pi.removeEventListener('zf:variant-change', this.onVariant);
          this.pi && this.pi.removeEventListener('change', this.onQtyInput);
          this.unsubQty && this.unsubQty();
        }

        get qtyInput() {
          return (
            (this.pi && this.pi.querySelector(`#Quantity-${ZF.sectionOf(this.pi)}`)) ||
            this.querySelector('[data-zf-pack-qty]')
          );
        }

        apply() {
          const radio = this.querySelector('.zf-ppack__radio:checked');
          const input = this.qtyInput;
          if (!radio || !input) return;
          if (String(input.value) === String(radio.value)) return;
          this.applying = true;
          input.value = radio.value;
          input.dispatchEvent(new Event('change', { bubbles: true }));
          this.applying = false;
        }

        syncRadios() {
          const input = this.qtyInput;
          if (!input) return;
          this.querySelectorAll('.zf-ppack__radio').forEach((r) => {
            r.checked = String(r.value) === String(input.value);
          });
        }

        render() {
          const fmt = this.dataset.moneyFormat;
          const unitBase = Math.max(this.price, this.compare);
          this.querySelectorAll('.zf-ppack__card').forEach((card) => {
            const qty = parseInt(card.dataset.qty, 10) || 1;
            const disc = parseFloat(card.dataset.discount) || 0;
            const total = Math.round((this.price * qty * (100 - disc)) / 100);
            const compareTotal = unitBase * qty;
            const totalEl = card.querySelector('[data-zf-pack-total]');
            const compareEl = card.querySelector('[data-zf-pack-compare]');
            const unitEl = card.querySelector('[data-zf-pack-unit]');
            if (totalEl) totalEl.textContent = ZF.formatMoney(total, fmt);
            if (compareEl) {
              compareEl.textContent = ZF.formatMoney(compareTotal, fmt);
              compareEl.hidden = compareTotal <= total;
            }
            if (unitEl) unitEl.textContent = ZF.formatMoney(total / qty, fmt);
          });
        }
      }
    );
  }

  /* ---------- <zf-addons>: add checked add-ons after the main product is added ---------- */
  if (!customElements.get('zf-addons')) {
    customElements.define(
      'zf-addons',
      class ZfAddons extends HTMLElement {
        connectedCallback() {
          if (this.closest('quick-add-modal')) return;
          this.pi = this.closest('product-info');
          this.form = this.pi && this.pi.querySelector(`#product-form-${ZF.sectionOf(this.pi)}`);
          if (hasPubSub) this.unsubCart = subscribe(PUB_SUB_EVENTS.cartUpdate, (evt) => this.onCartUpdate(evt));
          this.onSubmit = this.onSubmit.bind(this);
          this.form && this.form.addEventListener('submit', this.onSubmit, true);
        }

        disconnectedCallback() {
          this.unsubCart && this.unsubCart();
          this.form && this.form.removeEventListener('submit', this.onSubmit, true);
        }

        selectedItems() {
          return Array.from(this.querySelectorAll('.zf-paddons__input:checked')).map((i) => ({
            id: parseInt(i.value, 10),
            quantity: 1,
          }));
        }

        onCartUpdate(evt) {
          if (!evt || evt.source !== 'product-form' || !this.form) return;
          const idInput = this.form.querySelector('[name="id"]');
          if (!idInput || String(evt.productVariantId) !== String(idInput.value)) return;
          const items = this.selectedItems();
          if (!items.length || typeof window.zfAddToCart !== 'function') return;
          const opener = this.form.querySelector('[name="add"]');
          return window.zfAddToCart(items, opener).catch((err) => console.error('[zf-addons]', err));
        }

        /* No cart drawer/notification: Dawn redirects to /cart before pub/sub fires, so add everything here. */
        onSubmit(e) {
          if (document.querySelector('cart-drawer, cart-notification')) return;
          const items = this.selectedItems();
          if (!items.length) return;
          e.preventDefault();
          e.stopImmediatePropagation();
          const headers = { 'X-Requested-With': 'XMLHttpRequest', Accept: 'application/javascript' };
          fetch(routes.cart_add_url, { method: 'POST', headers, body: new FormData(this.form) })
            .then((r) => r.json())
            .then((res) => {
              if (res.status) throw new Error(res.description || res.message);
              return fetch(`${routes.cart_add_url}.js`, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
                body: JSON.stringify({ items }),
              });
            })
            .then(() => {
              window.location.href = routes.cart_url;
            })
            .catch((err) => {
              console.error('[zf-addons]', err);
              window.location.href = routes.cart_url;
            });
        }
      }
    );
  }

  /* ---------- <zf-gifts>: free gift tiers with cart-total progress ---------- */
  if (!customElements.get('zf-gifts')) {
    customElements.define(
      'zf-gifts',
      class ZfGifts extends HTMLElement {
        connectedCallback() {
          this.refresh();
          if (hasPubSub) this.unsubCart = subscribe(PUB_SUB_EVENTS.cartUpdate, () => this.refresh());
        }

        disconnectedCallback() {
          this.unsubCart && this.unsubCart();
        }

        refresh() {
          ZF.fetchCart()
            .then((cart) => this.render(cart.total_price || 0))
            .catch(() => {});
        }

        render(total) {
          const rate = ZF.currencyRate();
          const fmt = this.dataset.moneyFormat;
          const tiers = Array.from(this.querySelectorAll('.zf-pgifts__tier')).map((el) => ({
            el,
            cents: Math.round((parseFloat(el.dataset.threshold) || 0) * 100 * rate),
            title: el.dataset.title || '',
          }));
          if (!tiers.length) return;
          const max = Math.max(...tiers.map((t) => t.cents), 1);
          const pct = Math.min(100, Math.round((total / max) * 100));
          const fill = this.querySelector('[data-zf-gift-fill]');
          if (fill) fill.style.width = `${pct}%`;
          const bar = this.querySelector('[role="progressbar"]');
          if (bar) bar.setAttribute('aria-valuenow', pct);
          tiers.forEach((t) => {
            const unlocked = total >= t.cents;
            t.el.classList.toggle('is-unlocked', unlocked);
            const state = t.el.querySelector('[data-zf-gift-state]');
            if (state) state.textContent = unlocked ? 'Unlocked' : 'Locked';
          });
          const next = tiers.filter((t) => total < t.cents).sort((a, b) => a.cents - b.cents)[0];
          const msg = this.querySelector('[data-zf-gift-msg]');
          if (!msg) return;
          if (next) {
            msg.textContent = (this.dataset.lockedText || '')
              .replace('[amount]', ZF.formatMoney(next.cents - total, fmt))
              .replace('[gift]', next.title);
          } else {
            msg.textContent = this.dataset.unlockedText || '';
          }
        }
      }
    );
  }

  /* ---------- <zf-delivery>: Ordered -> Shipped -> Delivered dates ---------- */
  if (!customElements.get('zf-delivery')) {
    customElements.define(
      'zf-delivery',
      class ZfDelivery extends HTMLElement {
        connectedCallback() {
          const skip = this.dataset.skipWeekends === 'true';
          const ship = parseInt(this.dataset.shipDays, 10) || 0;
          const min = parseInt(this.dataset.minDays, 10) || 0;
          const max = parseInt(this.dataset.maxDays, 10) || 0;
          const today = new Date();
          const add = (n) => {
            const d = new Date(today.getFullYear(), today.getMonth(), today.getDate());
            let added = 0;
            while (added < n) {
              d.setDate(d.getDate() + 1);
              const day = d.getDay();
              if (!skip || (day !== 0 && day !== 6)) added += 1;
            }
            return d;
          };
          const lang = document.documentElement.lang || undefined;
          const fmt = (d) => {
            try {
              return d.toLocaleDateString(lang, { month: 'short', day: 'numeric' });
            } catch (e) {
              return d.toDateString().slice(4, 10);
            }
          };
          const delivered = max > min ? `${fmt(add(min))} – ${fmt(add(max))}` : fmt(add(min));
          const set = (key, text) => {
            const el = this.querySelector(`[data-zf-date="${key}"]`);
            if (el) el.textContent = text;
          };
          set('ordered', fmt(today));
          set('shipped', fmt(add(ship)));
          set('delivered', delivered);
          const headline = this.querySelector('[data-zf-headline]');
          if (headline && headline.dataset.template) {
            headline.textContent = headline.dataset.template.replace('[date]', delivered);
            headline.hidden = false;
          }
        }
      }
    );
  }

  /* ---------- <zf-sticky-atc>: sticky add-to-cart bar ---------- */
  if (!customElements.get('zf-sticky-atc')) {
    customElements.define(
      'zf-sticky-atc',
      class ZfStickyAtc extends HTMLElement {
        connectedCallback() {
          if (this.closest('quick-add-modal')) return;
          this.pi = this.closest('product-info');
          if (!this.pi) return;
          this.section = ZF.sectionOf(this.pi);
          this.btn = this.querySelector('[data-zf-sticky-add]');
          this.label = this.querySelector('[data-zf-sticky-label]');
          this.select = this.querySelector('[data-zf-sticky-select]');
          try {
            this.variants = JSON.parse((this.querySelector('[data-zf-sticky-variants]') || {}).textContent || '[]');
          } catch (e) {
            this.variants = [];
          }
          this.state = { past: false, footer: false };

          const anchor =
            this.pi.querySelector(`#ProductSubmitButton-${this.section}`) ||
            this.pi.querySelector('.product-form__buttons') ||
            this.pi.querySelector('.product__info-container');
          this.footer = document.querySelector('.shopify-section-group-footer-group, footer');

          if ('IntersectionObserver' in window && anchor) {
            this.io = new IntersectionObserver((entries) => {
              entries.forEach((en) => {
                if (en.target === this.footer) this.state.footer = en.isIntersecting;
                else this.state.past = !en.isIntersecting && en.boundingClientRect.bottom < 0;
              });
              this.toggle();
            });
            this.io.observe(anchor);
            if (this.footer) this.io.observe(this.footer);
          }

          this.onClick = () => this.add();
          this.onSelect = () => this.selectVariant(this.select.value);
          this.onVariant = (e) => this.handleVariant(e.detail && e.detail.variant);
          this.onCartDone = () => this.setLoading(false);
          this.btn && this.btn.addEventListener('click', this.onClick);
          this.select && this.select.addEventListener('change', this.onSelect);
          this.pi.addEventListener('zf:variant-change', this.onVariant);
          if (hasPubSub) {
            this.unsubs = [
              subscribe(PUB_SUB_EVENTS.cartUpdate, this.onCartDone),
              subscribe(PUB_SUB_EVENTS.cartError, this.onCartDone),
            ];
          }
          // Mirror the main button's sold-out / unavailable / label state (covers paths that skip variantChange).
          const main = this.mainButton;
          if (main && 'MutationObserver' in window) {
            this.mo = new MutationObserver(() => this.syncButton());
            this.mo.observe(main, {
              attributes: true,
              attributeFilter: ['disabled'],
              childList: true,
              characterData: true,
              subtree: true,
            });
          }
          requestAnimationFrame(() => this.syncButton());
        }

        disconnectedCallback() {
          this.io && this.io.disconnect();
          this.mo && this.mo.disconnect();
          this.btn && this.btn.removeEventListener('click', this.onClick);
          this.select && this.select.removeEventListener('change', this.onSelect);
          this.pi && this.pi.removeEventListener('zf:variant-change', this.onVariant);
          (this.unsubs || []).forEach((u) => u());
          document.body.classList.remove('zf-sticky-atc-on');
        }

        get mainButton() {
          return this.pi.querySelector(`#ProductSubmitButton-${this.section}`);
        }

        toggle() {
          const show = this.state.past && !this.state.footer;
          this.classList.toggle('is-visible', show);
          this.setAttribute('aria-hidden', show ? 'false' : 'true');
          this.inert = !show;
          if (show) this.removeAttribute('inert');
          else this.setAttribute('inert', '');
          document.body.classList.toggle('zf-sticky-atc-on', show);
        }

        setLoading(on) {
          if (!this.btn) return;
          this.btn.classList.toggle('loading', on);
          this.btn.setAttribute('aria-busy', on ? 'true' : 'false');
          clearTimeout(this.loadingTimer);
          if (on) this.loadingTimer = setTimeout(() => this.setLoading(false), 6000);
        }

        add() {
          const main = this.mainButton;
          if (main) {
            if (main.hasAttribute('disabled') || main.getAttribute('aria-disabled') === 'true') return;
            this.setLoading(true);
            main.click();
            return;
          }
          const id = parseInt((this.select && this.select.value) || this.dataset.variantId, 10);
          if (!id || typeof window.zfAddToCart !== 'function') return;
          this.setLoading(true);
          window
            .zfAddToCart([{ id, quantity: 1 }], this.btn)
            .catch((err) => console.error('[zf-sticky-atc]', err))
            .finally(() => this.setLoading(false));
        }

        selectVariant(id) {
          const variant = this.variants.find((v) => String(v.id) === String(id));
          const picker = this.pi.querySelector('variant-selects');
          if (!variant) return;
          if (!picker) {
            // No variant picker block: update the form's variant id directly.
            const input = this.pi.querySelector(`#product-form-${this.section} [name="id"]`);
            if (input) input.value = variant.id;
            return;
          }
          const groups = picker.querySelectorAll('fieldset, .product-form__input--dropdown');
          let last = null;
          groups.forEach((group, index) => {
            const value = variant.options[index];
            if (value === undefined) return;
            const radio = Array.from(group.querySelectorAll('input[type="radio"]')).find((r) => r.value === value);
            if (radio) {
              if (!radio.checked) {
                radio.checked = true;
                last = radio;
              }
              return;
            }
            const select = group.querySelector('select');
            if (!select) return;
            const option = Array.from(select.options).find((o) => o.value === value);
            if (option && !option.hasAttribute('selected')) {
              Array.from(select.options).forEach((o) => o.removeAttribute('selected'));
              option.setAttribute('selected', 'selected');
              select.value = value;
              last = select;
            }
          });
          if (!last) return;
          this.restoreScroll = window.scrollY;
          last.dispatchEvent(new Event('change', { bubbles: true }));
        }

        handleVariant(variant) {
          if (variant) this.dataset.variantId = variant.id;
          if (variant && this.select) this.select.value = String(variant.id);
          this.syncButton(variant);
          if (this.restoreScroll !== undefined && this.restoreScroll !== null) {
            const y = this.restoreScroll;
            this.restoreScroll = null;
            // Dawn focuses the changed option input after re-render, which scrolls; undo that for sticky changes.
            setTimeout(() => {
              window.scrollTo({ top: y });
              this.select && this.select.focus({ preventScroll: true });
            }, 0);
          }
        }

        syncButton(variant) {
          if (!this.btn) return;
          const main = this.mainButton;
          let disabled;
          let text;
          if (main) {
            disabled = main.hasAttribute('disabled');
            const span = main.querySelector('span');
            text = span ? span.textContent.trim() : '';
          } else if (variant) {
            disabled = !variant.available;
          }
          if (disabled !== undefined) this.btn.disabled = disabled;
          if (text && this.label) this.label.textContent = text;
        }
      }
    );
  }
})();
